import { Fragment } from 'react';
import { Link } from 'react-router-dom';

/**
 * Renders the TipTap JSON produced by the admin editor.
 *
 * Deliberately a React tree rather than generateHTML +
 * dangerouslySetInnerHTML: the content is authored in an admin editor
 * that accepts arbitrary link and image URLs, so rendering it as real
 * elements means nothing in a post can inject markup, and every node
 * gets marketing-namespaced classes instead of a global prose reset.
 *
 * Unknown node types fall through to their children rather than
 * disappearing, so a future editor extension degrades instead of
 * silently dropping a paragraph.
 */

interface TipTapMark {
  type?: string;
  attrs?: Record<string, unknown>;
}

export interface TipTapNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: TipTapMark[];
  content?: TipTapNode[];
}

/** Blocks javascript:/data: hrefs an admin could paste into the editor. */
function safeHref(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const href = raw.trim();
  if (!href) return undefined;
  if (/^(?:javascript|data|vbscript):/i.test(href)) return undefined;
  return href;
}

function isExternal(href: string): boolean {
  return /^https?:\/\//i.test(href) && !/^https?:\/\/(www\.)?inafana\.com/i.test(href);
}

function renderText(node: TipTapNode, key: React.Key): React.ReactNode {
  let el: React.ReactNode = node.text ?? '';

  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
        el = <strong>{el}</strong>;
        break;
      case 'italic':
        el = <em>{el}</em>;
        break;
      case 'strike':
        el = <s>{el}</s>;
        break;
      case 'code':
        el = <code>{el}</code>;
        break;
      case 'link': {
        const href = safeHref(mark.attrs?.href);
        if (!href) break;
        if (isExternal(href)) {
          el = (
            <a href={href} target="_blank" rel="noreferrer noopener">
              {el}
            </a>
          );
        } else if (href.startsWith('/')) {
          // Cross-links between posts route client-side instead of
          // reloading the whole app.
          el = <Link to={href}>{el}</Link>;
        } else {
          el = <a href={href}>{el}</a>;
        }
        break;
      }
      default:
        break;
    }
  }

  return <Fragment key={key}>{el}</Fragment>;
}

function renderChildren(nodes: TipTapNode[] | undefined): React.ReactNode {
  return (nodes ?? []).map((child, i) => renderNode(child, i));
}

function renderNode(node: TipTapNode, key: React.Key): React.ReactNode {
  if (node.type === 'text') return renderText(node, key);

  const kids = renderChildren(node.content);

  switch (node.type) {
    case 'paragraph':
      return <p key={key}>{kids}</p>;

    case 'heading': {
      // The post title owns the page's h1, so an editor h1 is demoted
      // to h2 and the rest keep their level — headings stay sequential.
      const raw = Number(node.attrs?.level ?? 2);
      const level = Math.min(Math.max(raw <= 1 ? 2 : raw, 2), 4);
      const Tag = `h${level}` as 'h2' | 'h3' | 'h4';
      return <Tag key={key}>{kids}</Tag>;
    }

    case 'bulletList':
      return <ul key={key}>{kids}</ul>;

    case 'orderedList': {
      const start = Number(node.attrs?.start ?? 1);
      return (
        <ol key={key} start={Number.isFinite(start) && start !== 1 ? start : undefined}>
          {kids}
        </ol>
      );
    }

    case 'listItem':
      return <li key={key}>{kids}</li>;

    case 'blockquote':
      return <blockquote key={key}>{kids}</blockquote>;

    case 'codeBlock':
      return (
        <pre key={key}>
          <code>{kids}</code>
        </pre>
      );

    case 'horizontalRule':
      return <hr key={key} />;

    case 'hardBreak':
      return <br key={key} />;

    case 'image': {
      const src = safeHref(node.attrs?.src);
      if (!src) return null;
      const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
      const title = typeof node.attrs?.title === 'string' ? node.attrs.title : undefined;
      return (
        <figure key={key}>
          <img src={src} alt={alt} loading="lazy" />
          {title && <figcaption>{title}</figcaption>}
        </figure>
      );
    }

    case 'doc':
      return <Fragment key={key}>{kids}</Fragment>;

    default:
      return <Fragment key={key}>{kids}</Fragment>;
  }
}

export function RichText({ content }: { content: unknown }): React.JSX.Element {
  const doc = (content ?? {}) as TipTapNode;
  // Not `.prose` — the admin editor already uses that class name.
  return <div className="post-body">{renderChildren(doc.content)}</div>;
}

/** Word count across every text node, for the reading-time estimate. */
export function countWords(content: unknown): number {
  let words = 0;
  const walk = (node: TipTapNode): void => {
    if (typeof node.text === 'string') {
      const trimmed = node.text.trim();
      if (trimmed) words += trimmed.split(/\s+/).length;
    }
    for (const child of node.content ?? []) walk(child);
  };
  walk((content ?? {}) as TipTapNode);
  return words;
}
